import { useEffect, useState } from 'react';
import { useAuth } from '../../../Contexts/AuthProvider';
import { useTemplate } from '../../../features/template/TemplateProvider';
import { ButtonPrimary } from '../../../utils/Button';
import FocusControl from '../../../utils/FocusControl';
import InputField from '../../../utils/InputField';
import cn from '../../../utils/cn';
import { handleSendEmailOpe, handleSubmitOtpOpe } from '../SecurityOperation';

function ResendButton({ handleResendClick, className }) {
    const [timeCount, setTimeCount] = useState(30);

    useEffect(() => {
        const interval = setInterval(() => {
            if (timeCount >= 1) {
                setTimeCount(timeCount - 1);
            }
        }, 1000);

        return () => clearInterval(interval);
    }, [timeCount]);
    return (
        <button
            type="button"
            className={cn(
                `h-10 text-sm w-[120px] uppercase text-primary-700 rounded-lg leading-10 transition-colors bg-primary-50 pointer-events-none cursor-default`,
                className,
                {
                    'bg-secondary-200 text-secondary cursor-pointer pointer-events-auto':
                        timeCount === 0
                }
            )}
            onClick={() => {
                if (timeCount === 0) {
                    handleResendClick();
                    setTimeCount(60);
                }
            }}
        >
            {timeCount === 0 ? 'Resend Code' : `Resend in ${timeCount}s`}
        </button>
    );
}

function Step2({ show, formData, handleChange, handleResendClick, handleSubmitClick, loading }) {
    return (
        <form
            onSubmit={handleSubmitClick}
            className={`overflow-hidden transition-all duration-500 relative ${
                show ? 'right-0' : '-right-full h-0'
            }`}
        >
            <div className="mb-3">
                <InputField
                    label="Enter OTP"
                    name="Otp"
                    formData={formData}
                    onChange={handleChange}
                />
            </div>

            <div className="flex justify-end">
                <div className="flex items-center gap-x-2">
                    <ResendButton handleResendClick={handleResendClick} className="h-9" />
                    <ButtonPrimary title="Submit" btnType="submit" loading={loading} />
                </div>
            </div>
        </form>
    );
}

function Step1({ show, value, handleSendClick, loading }) {
    return (
        <form
            onSubmit={handleSendClick}
            className={`overflow-hidden transition-all duration-500 relative ${
                show ? 'left-0' : '-left-full h-0'
            }`}
        >
            <p className="text-sm font-normal mb-3">
                To enhance the security of your account, please verify your email address by
                entering the code we`ll send to your primary email.
            </p>
            <div className="mb-3">
                <input
                    defaultValue={value}
                    readOnly
                    autoComplete="off"
                    className={` flex-shrink w-full h-[40px] bg-[#f3f3f3] rounded-lg block outline-none border border-primary-200 px-2 md:px-3 text-primary text-sm leading-6 font-normal disabled:bg-primary-50 disabled:text-primary-700 `}
                />
            </div>

            <div className="flex justify-end">
                <ButtonPrimary title="Send Code" btnType="submit" loading={loading} />
            </div>
        </form>
    );
}

function TwoFacAuthModal({ closeModal }) {
    const [step, setStep] = useState(1); // 1 for send email, 2 for submit otp
    const [formData, setFormData] = useState({ Secret: '', Otp: '' });
    const [loading, setLoading] = useState(false);
    const [isError, setIsError] = useState(''); // for error message

    const { authInfo, authStateUpdated } = useAuth();

    const { setMessage } = useTemplate();

    // handler for input field change
    const handleChange = (ev) => {
        const { name, value } = ev.target;
        setFormData((prevDt) => ({ ...prevDt, [name]: value }));
    };

    // handler for send email
    const handleSendClick = (ev) => {
        ev?.preventDefault();
        setLoading(true);
        handleSendEmailOpe(setIsError)
            .then(({ title, data }) => {
                setFormData({ Secret: data.secret });
                setMessage({ type: 'success', text: title });
                setLoading(false);
                setStep(2);
            })
            .catch((err) => {
                setMessage({ type: 'error', text: err.toString() });
            });
    };

    // handler for submit otp
    const handleSubmitClick = (e) => {
        e?.preventDefault();
        setLoading(true);
        const payload = {
            Secret: formData.Secret,
            Otp: formData.Otp
        };

        handleSubmitOtpOpe(payload, setIsError)
            .then(({ title }) => {
                setMessage({ type: 'success', text: title });
                closeModal();
                authStateUpdated();
            })
            .catch((err) => {
                setLoading(false);
                setMessage({ type: 'error', text: err.toString() });
            });
    };
    FocusControl(isError, setIsError, formData);
    return (
        <div className="overflow-hidden">
            <Step1
                show={step === 1}
                loading={loading}
                value={authInfo?.data?.primaryEmail}
                handleSendClick={handleSendClick}
                step={step}
            />
            <Step2
                show={step === 2}
                loading={loading}
                formData={formData}
                handleChange={handleChange}
                handleResendClick={handleSendClick}
                handleSubmitClick={handleSubmitClick}
            />
        </div>
    );
}

export default TwoFacAuthModal;
